import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  InputGroup,
  InputMessage,
  InputText,
  Stack,
  Text,
} from "@aksara-ui/react";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { storeQuestBadge, updateQuestBadge } from "redux/actions/quest";
import { InputSelect } from "components/Form/InputSelect";
import ImageDropzone from "components/ImageDropzone";
import styled from "styled-components";
import * as yup from "yup";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

interface Props {
  missionId: string;
  badgeId: string;
}

const TIER_OPTIONS = [
  { label: "Bronze", value: "Bronze" },
  { label: "Silver", value: "Silver" },
  { label: "Gold", value: "Gold" },
  { label: "Platinum", value: "Platinum" },
  { label: "Foil", value: "Foil" },
];

const initialValues = {
  name: "",
  tier: "",
  min_visits: 1,
  reward_points: 0,
  sort_order: 1,
  description: "",
  tier_image_active: "",
  tier_image_inactive: "",
  is_active: true,
};

const BadgeFormModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  missionId,
  badgeId,
}) => {
  const isAddMode = !badgeId;
  const dispatch = useDispatch();
  const formikRef = useRef<FormikValues>() as any;

  const { questBadges, isFormLoading } = useSelector(
    (state: any) => state.questReducer
  );
  const { appUrl } = useSelector((state: any) => state.globalReducer);

  const [filesBadgeActive, setFilesBadgeActive] = useState<any[]>([]);
  const [filesBadgeInactive, setFilesBadgeInactive] = useState<any[]>([]);

  const handleCloseModal = () => {
    setFilesBadgeActive([]);
    setFilesBadgeInactive([]);
    onClose && onClose();
  };

  const handleSubmit = (values: any) => {
    const badgeActiveFile =
      typeof filesBadgeActive[0] === "object"
        ? filesBadgeActive[0]
        : typeof filesBadgeActive[0] === "string"
          ? filesBadgeActive[0]
          : null;

    const badgeInactiveFile =
      typeof filesBadgeInactive[0] === "object"
        ? filesBadgeInactive[0]
        : typeof filesBadgeInactive[0] === "string"
          ? filesBadgeInactive[0]
          : null;

    const payload: any = {
      ...values,
      min_visits: Number(values.min_visits),
      reward_points: Number(values.reward_points),
      sort_order: Number(values.sort_order),
      is_active: values.is_active ? 1 : 0,
    };

    if (badgeActiveFile) {
      payload.tier_image_active = badgeActiveFile;
    }
    if (badgeInactiveFile) {
      payload.tier_image_inactive = badgeInactiveFile;
    }

    isAddMode
      ? dispatch(storeQuestBadge(missionId, payload, handleCloseModal) as any)
      : dispatch(updateQuestBadge(badgeId, missionId, payload, handleCloseModal) as any);
  };

  useEffect(() => {
    if (!isAddMode && isOpen) {
      const detail = questBadges.find(
        (item: any) => String(item.id) === String(badgeId)
      );
      if (detail && formikRef.current) {
        formikRef.current.setValues({
          name: detail.name ?? "",
          tier: detail.tier ?? "",
          min_visits: detail.min_visits ?? 1,
          reward_points: detail.reward_points ?? 0,
          sort_order: detail.sort_order ?? 1,
          description: detail.description ?? "",
          tier_image_active: detail.tier_image_active ?? "",
          tier_image_inactive: detail.tier_image_inactive ?? "",
          is_active: detail.is_active ?? true,
        });

        setFilesBadgeActive(
          detail.tier_image_active
            ? [
              detail.tier_image_active.startsWith("http")
                ? detail.tier_image_active
                : `${appUrl}/storage/${detail.tier_image_active}`,
            ]
            : []
        );
        setFilesBadgeInactive(
          detail.tier_image_inactive
            ? [
              detail.tier_image_inactive.startsWith("http")
                ? detail.tier_image_inactive
                : `${appUrl}/storage/${detail.tier_image_inactive}`,
            ]
            : []
        );
      }
    } else if (isAddMode) {
      setFilesBadgeActive([]);
      setFilesBadgeInactive([]);
      if (formikRef.current) {
        formikRef.current.resetForm();
      }
    }
  }, [isAddMode, questBadges, badgeId, isOpen, appUrl]);

  const validationSchema = yup.object({
    name: yup.string().required("Nama badge wajib diisi"),
    tier: yup.string().required("Tier wajib dipilih"),
    min_visits: yup.number().min(1).required("Min. kunjungan wajib diisi"),
    reward_points: yup.number().min(0).required("Reward points wajib diisi"),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <DialogHeader>
              <Text fontWeight={700}>
                {isAddMode ? "Tambah Badge Tier" : "Edit Badge Tier"}
              </Text>
            </DialogHeader>
            <DialogContent>
              <FormWrapper>
                <Field name="name">
                  {({ field, meta }: FieldProps<string>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="name">Nama Badge</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText
                          type="text"
                          size={40}
                          errors={!!meta.error && meta.touched}
                          placeholder="contoh: Bronze Thrill"
                          {...field}
                        />
                      </InputGroup>
                      {!!meta.error && meta.touched && (
                        <InputMessage variant="error">{meta.error}</InputMessage>
                      )}
                    </Stack>
                  )}
                </Field>

                <Stack spacing="xs">
                  <FormLabel>Tier</FormLabel>
                  <InputSelect
                    label=""
                    placeholder="Pilih Tier"
                    items={TIER_OPTIONS}
                    selectedItem={TIER_OPTIONS.find((o) => o.value === values.tier) ?? null}
                    itemToString={(item: any) => item?.label ?? ""}
                    handleSelectedItemChange={({ selectedItem }: any) =>
                      setFieldValue("tier", selectedItem?.value ?? "")
                    }
                  />
                </Stack>

                <Field name="min_visits">
                  {({ field, meta }: FieldProps<number>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="min_visits">Min. Kunjungan</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText
                          type="number"
                          size={40}
                          errors={!!meta.error && meta.touched}
                          {...field}
                        />
                      </InputGroup>
                      {!!meta.error && meta.touched && (
                        <InputMessage variant="error">{meta.error}</InputMessage>
                      )}
                    </Stack>
                  )}
                </Field>

                <Field name="reward_points">
                  {({ field, meta }: FieldProps<number>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="reward_points">Reward Points</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText
                          type="number"
                          size={40}
                          errors={!!meta.error && meta.touched}
                          {...field}
                        />
                      </InputGroup>
                      {!!meta.error && meta.touched && (
                        <InputMessage variant="error">{meta.error}</InputMessage>
                      )}
                    </Stack>
                  )}
                </Field>

                <Field name="description">
                  {({ field }: FieldProps<string>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="description">Deskripsi</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText type="text" size={40} {...field} />
                      </InputGroup>
                    </Stack>
                  )}
                </Field>

                {/* Badge Image Active */}
                <Stack spacing="xs">
                  <FormLabel>Badge Image (Active)</FormLabel>
                  <ImageDropzone
                    files={filesBadgeActive}
                    setFiles={setFilesBadgeActive}
                  />
                </Stack>

                {/* Badge Image Inactive */}
                <Stack spacing="xs">
                  <FormLabel>Badge Image (Inactive)</FormLabel>
                  <ImageDropzone
                    files={filesBadgeInactive}
                    setFiles={setFilesBadgeInactive}
                  />
                </Stack>
              </FormWrapper>
            </DialogContent>
            <DialogFooter>
              <Stack
                display="flex"
                flexDirection="row"
                spacing="xs"
                flex={1}
                style={{ gap: 15 }}
              >
                <Button
                  variant="outline"
                  block
                  type="button"
                  onClick={() => handleCloseModal()}
                >
                  Batal
                </Button>
                <Button
                  variant="primary"
                  block
                  type="submit"
                  isLoading={isFormLoading}
                >
                  {isAddMode ? "Simpan" : "Perbarui"}
                </Button>
              </Stack>
            </DialogFooter>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default BadgeFormModal;
